import { Box, Flex, Heading } from '@chakra-ui/react';
import type { ReactNode } from 'react';

/**
 * Bordered, rounded, soft-shadowed section container with a header row
 * and a horizontal divider, sized for use inside drawers and detail
 * pages.
 *
 * Mirrors the inline pattern used across AIM admin "user" sections
 * (apps/aegix-aim-react/src/pages/admin/users/components/User*Section.tsx)
 * so call sites stop hand-rolling the same Box + Flex wrapper.
 *
 * Two ways to render the header:
 *
 *   - Convenience (`title` + optional `action`):
 *       <SectionCard title="Sites" action={<AddButton />}>...</SectionCard>
 *
 *   - Fully custom (`header`):
 *       <SectionCard header={<MyCustomHStack />}>...</SectionCard>
 *
 *   When both are passed, `header` wins. The horizontal border below
 *   the header is rendered by SectionCard either way.
 */
export interface SectionCardProps {
  /** Simple title text. Rendered as a Heading inside the header row. */
  title?: ReactNode;
  /** Right-side content for the simple title header (button, link, etc.). */
  action?: ReactNode;
  /** Replace the entire header content. Overrides `title` / `action`. */
  header?: ReactNode;
  children: ReactNode;
  /** Pass-through `data-e2e` for tests. */
  e2eTitle?: string;
}

/**
 * @useFor grouping a section of related content on a detail / form
 *   page — bordered surface, optional header with title + action
 *   button on the right.
 *
 *   Examples in-tree: Account settings sub-sections (Profile,
 *   Notifications, Sessions), People detail page (Roles, Schools,
 *   Activity).
 *
 *   For a clickable summary card use `<CardComponent>`. For metric
 *   cards (single number) use `<StatCard>`. This primitive is the
 *   "container with a title + slot" pattern.
 */
export function SectionCard({
  title,
  action,
  header,
  children,
  e2eTitle,
}: SectionCardProps) {
  const showHeader = header !== undefined || title !== undefined;

  return (
    <Box
      color="text.primary"
      bg="surface.primary"
      border="2px solid"
      borderColor="border.secondary"
      boxShadow="0px 2.5px 4px -1px rgba(0, 0, 0, 0.15)"
      borderRadius="8px"
      padding="20px"
      data-e2e={e2eTitle}
    >
      {showHeader && (
        <Flex
          justifyContent="space-between"
          alignItems="center"
          gap={3}
          borderBottom="1px solid"
          borderColor="border.secondary"
          pb={3}
          mb={3}
        >
          {header ?? (
            <>
              {typeof title === 'string' ? (
                <Heading as="h3" textStyle="h2" color="text.primary">
                  {title}
                </Heading>
              ) : (
                title
              )}
              {action}
            </>
          )}
        </Flex>
      )}
      {children}
    </Box>
  );
}

export default SectionCard;
